/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import MatterIndicators, { BOTTOM_LINE_IN_NEW_DESIGN } from 'components/matters/MatterIndicators';
import { FormattedDate } from 'components/misc/FormattedDate';
import PartyName from 'components/party/PartyName';
import { SearchTermHighlight } from 'components/SearchTermHighlight';
import { Tooltip } from 'components/ui/tooltip';
import { getLinks } from 'pages/matters/util';
import LabelledData from 'pages/timecards/LabelledData';
import { FormattedMessage } from 'react-intl';
import { CoiCheckDetailsJson, CoiCheckScopeType, EmpDto, VisibilityScope } from 'types/coi-check';
import { displayLocalNameIfExists } from '../util/CoiCheckUtil';

const RelationshipOwnersCard = ({ employees }: { employees?: EmpDto[] }): JSX.Element => (
    <div>
        <span className="text-caption font-semibold">
            <FormattedMessage id="coi.rel-owners" />
        </span>
        <LabelledData label="coi.rel-partners">
            {employees?.length
                ? getLinks(
                      'employee',
                      employees.filter((emp) => emp.partyEmployeeTypeCode === 'RELATIONSHIP_PARTNER')
                  )
                : ''}
        </LabelledData>
        <LabelledData label="coi.rel-managers">
            {employees?.length
                ? getLinks(
                      'employee',
                      employees.filter((emp) => emp.partyEmployeeTypeCode === 'RELATIONSHIP_MANAGER')
                  )
                : ''}
        </LabelledData>
    </div>
);

const ContactNameCard = ({ detailsJson: value }: { detailsJson: CoiCheckDetailsJson }): JSX.Element => (
    <>
        <div className="flex items-center font-medium">
            <PartyName
                party={{
                    ...value,
                    name:
                        value.officialFirstName && value.officialLastName
                            ? `${value.officialFirstName} ${value.officialLastName}`.trim() +
                              (displayLocalNameIfExists(value.localFirstName, value.localLastName) ?? '')
                            : (value.name ?? '') +
                              (displayLocalNameIfExists(value.localFirstName, value.localLastName) ?? ''),
                }}
                isLink
                useLinkWithOrigin
                variant="readonly-other"
            />
            <Tooltip content={<RelationshipOwnersCard employees={value.employees} />}>
                <span className="inline-flex">
                    <InfoOutlinedIcon className="text-grey-600 ml-[1px] h-[1.2rem]" />
                </span>
            </Tooltip>
        </div>
        {value.officialName ? (
            <LabelledData label="coi.official">
                <SearchTermHighlight text={value.officialName} />{' '}
                {value.officialLocalName ? (
                    <>
                        (<SearchTermHighlight text={value.officialLocalName} />)
                    </>
                ) : null}
            </LabelledData>
        ) : null}
    </>
);

const ContactNameCardDisabled = ({ detailsJson: value }: { detailsJson: CoiCheckDetailsJson }): JSX.Element => (
    <>
        <div className="flex items-center font-medium">
            <SearchTermHighlight
                text={
                    value.officialFirstName && value.officialLastName
                        ? `${value.officialFirstName} ${value.officialLastName}`.trim()
                        : value.name
                }
            />
            <SearchTermHighlight text={displayLocalNameIfExists(value.localFirstName, value.localLastName)} />
        </div>
        {value.officialName ? (
            <LabelledData label="coi.official">
                <SearchTermHighlight text={value.officialName} />{' '}
                {value.officialLocalName ? (
                    <>
                        (<SearchTermHighlight text={value.officialLocalName} />)
                    </>
                ) : null}
            </LabelledData>
        ) : null}
    </>
);

export const ContactOpeningCell = ({ detailsJson: value }: { detailsJson: CoiCheckDetailsJson }): JSX.Element => {
    return value.visibility === VisibilityScope.FULL ? (
        <div className="flex flex-col">
            <ContactNameCard detailsJson={value} />
            <LabelledData label="coi.group">
                {value.partyGroups?.length ? getLinks('group', value.partyGroups) : ''}
            </LabelledData>
            <LabelledData label="coi.previous">
                <SearchTermHighlight text={value.previousNames} />
            </LabelledData>
        </div>
    ) : (
        <div className="new:flex-wrap flex items-center gap-4">
            <MatterIndicators
                isConfidential={value.isConfidential}
                hasAccess={false}
                className={BOTTOM_LINE_IN_NEW_DESIGN}
            />
            <div className="text-caption text-fg-secondary font-medium">
                <ContactNameCardDisabled detailsJson={value} />
            </div>
        </div>
    );
};

export const OpponentOpeningCell = ({
    detailsJson: value,
}: {
    detailsJson: CoiCheckDetailsJson;
}): JSX.Element | null => {
    return value.visibility === VisibilityScope.FULL ? (
        <div className="flex flex-col">
            <ContactNameCard detailsJson={value} />
            <LabelledData label="coi.group">
                {value.partyGroups?.length ? getLinks('group', value.partyGroups) : ''}
            </LabelledData>
            <LabelledData label="coi.previous">
                <SearchTermHighlight text={value.previousNames} />
            </LabelledData>
            <LabelledData label="coi.email:">{value.email ?? ''}</LabelledData>
        </div>
    ) : (
        <div className="new:flex-wrap flex items-center gap-4">
            <MatterIndicators
                isConfidential={value.isConfidential}
                hasAccess={false}
                className={BOTTOM_LINE_IN_NEW_DESIGN}
            />
            <div className="text-caption text-fg-secondary font-medium">
                <ContactNameCardDisabled detailsJson={value} />
            </div>
        </div>
    );
};

export const IdentifiersCell = ({
    detailsJson: value,
    entityName,
    scopeType,
}: {
    detailsJson: CoiCheckDetailsJson;
    entityName: string;
    scopeType: CoiCheckScopeType;
}): JSX.Element | null => {
    const isCompany = entityName === 'PartyCompany';
    const showLastTimecardDate = scopeType === CoiCheckScopeType.CLIENT || scopeType === CoiCheckScopeType.OPPONENT;

    return value.visibility === VisibilityScope.FULL ? (
        <div className="flex flex-col">
            {isCompany ? (
                <>
                    <LabelledData label="coi.vat-no" className="text-body1">
                        {value.vatRegNum ?? ''}
                    </LabelledData>
                    <LabelledData label="coi.reg-code" className="text-body1">
                        {value.regCode ?? ''}
                    </LabelledData>
                </>
            ) : null}
            {!isCompany ? (
                <LabelledData label="coi.personal-id" className="text-body1">
                    {value.personalIdCode}
                </LabelledData>
            ) : null}
            {showLastTimecardDate && (
                <LabelledData label="coi.last-timecard-date" className="text-body1">
                    <FormattedDate value={value.lastTimecardDate} fallback="" />
                </LabelledData>
            )}
        </div>
    ) : null;
};

export const EmployeesAndBeneficialOwnersCell = ({
    detailsJson: value,
}: {
    detailsJson: CoiCheckDetailsJson;
}): JSX.Element | null => {
    if (value.visibility !== VisibilityScope.FULL) return null;

    return (
        <div className="flex flex-col">
            <LabelledData label="coi.employees-label">
                {value.partyEmployees?.length ? getLinks('contact', value.partyEmployees, { target: '_blank' }) : ''}
            </LabelledData>
            <LabelledData label="coi.beneficial-owners">
                {value.partyPersons?.length ? getLinks('contact', value.partyPersons, { target: '_blank' }) : ''}
            </LabelledData>
        </div>
    );
};

export const UboForCompaniesCell = ({
    detailsJson: value,
}: {
    detailsJson: CoiCheckDetailsJson;
}): JSX.Element | null => {
    if (value.visibility !== VisibilityScope.FULL) return null;

    return <div>{value.uboCompanies?.length ? getLinks('contact', value.uboCompanies, { target: '_blank' }) : ''}</div>;
};
